import React from "react";
import { cn } from "../ui";

/**
 * RoleTiles — the "who are you signing in as" picker on /login and /signup.
 *
 * One definition for both doors. They differ only in how many roles they offer
 * (four vs three), which is the `roles` prop; everything else — the geometry,
 * the selected tone, the announcement — has to be identical, because a user who
 * signs up as an Instructor and then signs in has to recognise the same control.
 *
 * ⚠️ It is NOT `PanelButton`. That primitive fixes its own border colour, and a
 * selected tile has to change it — overriding a colour utility from a className
 * is a source-order race that Tailwind decides by the order the colours are
 * declared in `tailwind.config.js`, not by which class was passed last. So the
 * two states each emit exactly ONE border-colour class here.
 *
 * `aria-pressed` rather than a radiogroup: this is a small set of toggles with
 * no roving focus, and every tile stays tab-reachable, which is what a keyboard
 * user expects on a login form.
 *
 * Props:
 *   roles     — [{ key, label, icon }] where `icon` is a lucide component
 *   value     — the selected `key`, or null
 *   onSelect  — called with the whole role object
 *   columns   — 2 (default) | 3
 *   labelId   — id of the visible label, for the group's accessible name
 */

const GRID_COLUMNS = {
  2: "tw-grid-cols-2",
  3: "tw-grid-cols-3",
};

const RoleTiles = ({ roles, value, onSelect, columns = 2, labelId }) => (
  <div role="group" aria-labelledby={labelId} className={cn("tw-grid tw-gap-3", GRID_COLUMNS[columns] || GRID_COLUMNS[2])}>
    {roles.map((role) => {
      const Icon = role.icon;
      const selected = value === role.key;
      return (
        <button
          key={role.key}
          type="button"
          aria-pressed={selected}
          onClick={() => onSelect(role)}
          className={cn(
            "tw-flex tw-cursor-pointer tw-items-center tw-justify-center tw-gap-2.5 tw-rounded-md",
            // border-solid is load-bearing: Preflight is off, so a bare border
            // utility is a width on a style that is still the UA's `outset`.
            "tw-border tw-border-solid tw-px-3 tw-py-3.5 tw-text-sm tw-font-medium",
            "tw-transition-colors tw-duration-150 tw-focus-ring",
            selected
              ? "tw-border-primary tw-bg-primary/10 tw-text-primary"
              : "tw-border-line tw-bg-surface-raised tw-text-content hover:tw-border-primary/50 hover:tw-bg-surface-hover"
          )}
        >
          {Icon && <Icon size={17} className="tw-shrink-0" aria-hidden="true" />}
          <span className="tw-truncate">{role.label}</span>
        </button>
      );
    })}
  </div>
);

export default RoleTiles;
